/**
 * The Bilal interaction mark: an abstract breathing form — a rounded core inside a fine gold halo —
 * never a human avatar. Movement always reflects real state: the halo brightens only while actual
 * listening/speaking is happening (amplitude arrives from a Web Audio analyser when wired), the
 * core breathes only while the pipeline genuinely works, and reduced-motion users get the static
 * accessible version via the stylesheet's media guard.
 */
export type MarkState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'completed' | 'error';

export function BilalMark({
  state = 'idle',
  depth = 'balanced',
  size = 28,
  amplitude = 0,
}: {
  readonly state?: MarkState;
  readonly depth?: 'quick' | 'balanced' | 'deep';
  readonly size?: number;
  /** 0..1 from a real analyser; scales the halo. Never decorative randomness. */
  readonly amplitude?: number;
}): React.JSX.Element {
  const haloScale = 1 + Math.min(Math.max(amplitude, 0), 1) * 0.25;
  return (
    <span className="bilal-mark" data-state={state} data-depth={depth} aria-hidden="true">
      <svg width={size} height={size} viewBox="0 0 32 32">
        <circle
          className="halo"
          cx="16"
          cy="16"
          r="13"
          style={{ transform: `scale(${String(haloScale)})` }}
        />
        <path
          className="core"
          d="M16 6c4.5 0 8 3.2 8 8.4 0 5.8-3.6 9.6-8 11.6-4.4-2-8-5.8-8-11.6C8 9.2 11.5 6 16 6Z"
        />
      </svg>
    </span>
  );
}
